import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { SynapsMediaApiService } from '../../synaps-media-api.service';
import * as DomainsActions from './domains.actions';
import { ErrorCode, ErrorResponse } from '../../synaps-media.types';
import { showToast } from 'src/app/auth/state/toast/toast.actions';

@Injectable()
export class DomainsEffects {
  constructor(
    private actions$: Actions,
    private api: SynapsMediaApiService
  ) {}

  loadDomains$ = createEffect(() => this.actions$.pipe(
    ofType(DomainsActions.loadDomains),
    switchMap(() => this.api.getUserDomains().pipe(
      map(result => result.data),
      map(domains => DomainsActions.loadDomainsSuccess({ domains })),
      catchError((error: ErrorResponse) => of(DomainsActions.loadDomainsFailure({
        error: error.errors[0] || { code: ErrorCode.InternalServerError }
      })))
    ))
  ));

  loadDomain$ = createEffect(() => this.actions$.pipe(
    ofType(DomainsActions.loadDomain),
    switchMap(({ domain }) => this.api.getUserDomain(domain).pipe(
      map(result => result.data),
      map(domain => DomainsActions.loadDomainSuccess({ domain })),
      catchError((error: ErrorResponse) => of(DomainsActions.loadDomainFailure({
        error: error.errors[0] || { code: ErrorCode.InternalServerError }
      })))
    ))
  ));

  addDomainRecord$ = createEffect(() => this.actions$.pipe(
    ofType(DomainsActions.addDomainRecord),
    mergeMap(({ domain, record }) => this.api.addUserDomainRecord(domain, record).pipe(
      map(record => DomainsActions.addDomainRecordSuccess({ record, domain })),
      catchError((error: ErrorResponse) => of(DomainsActions.addDomainRecordFailure({
        error: error.errors[0] || { code: ErrorCode.InternalServerError }
      })))
    ))
  ));

  updateDomainRecord$ = createEffect(() => this.actions$.pipe(
    ofType(DomainsActions.updateDomainRecord),
    mergeMap(({ domain, recordId, record }) => this.api.updateUserDomainRecord(domain, recordId, record).pipe(
      map(record => DomainsActions.updateDomainRecordSuccess({ record, domain })),
      catchError((error: ErrorResponse) => of(DomainsActions.updateDomainRecordFailure({
        error: error.errors[0] || { code: ErrorCode.InternalServerError }
      })))
    ))
  ));

  deleteDomainRecord$ = createEffect(() => this.actions$.pipe(
    ofType(DomainsActions.deleteDomainRecord),
    mergeMap(({ domain, recordId }) => this.api.deleteUserDomainRecord(domain, recordId).pipe(
      map(() => DomainsActions.deleteDomainRecordSuccess({ domain, recordId })),
      catchError((error: ErrorResponse) => of(DomainsActions.deleteDomainRecordFailure({
        error: error.errors[0] || { code: ErrorCode.InternalServerError }
      })))
    ))
  ));

  // Show toast on success
  recordSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(
      DomainsActions.addDomainRecordSuccess,
      DomainsActions.updateDomainRecordSuccess,
      DomainsActions.deleteDomainRecordSuccess
    ),
    map((action) => {
      let message = 'Operation completed successfully';

      if (action.type === DomainsActions.addDomainRecordSuccess.type) {
        message = 'Domain record added successfully';
      } else if (action.type === DomainsActions.updateDomainRecordSuccess.type) {
        message = 'Domain record updated successfully';
      } else if (action.type === DomainsActions.deleteDomainRecordSuccess.type) {
        message = 'Domain record deleted successfully';
      }

      return showToast({
        message,
        color: 'success'
      });
    })
  ));

  // Show toast on error
  domainError$ = createEffect(() => this.actions$.pipe(
    ofType(
      DomainsActions.loadDomainsFailure,
      DomainsActions.loadDomainFailure,
      DomainsActions.addDomainRecordFailure,
      DomainsActions.updateDomainRecordFailure,
      DomainsActions.deleteDomainRecordFailure
    ),
    map(({ error }) => showToast({
      message: `Error: ${error.message || error.code}`
    }))
  ));
}
